import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import HomeImg from "../assets/IMG_5439.jpg";
// Import your resume PDF
import ResumePDF from '../assets/resume.pdf';

const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  position: relative;
  padding: 4rem 2rem;
  background: ${({ theme }) => theme.background};
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 2rem 1rem;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 45%;
    height: 100%;
    background: ${({ theme }) => theme.gradient};
    opacity: 0.05;
    clip-path: polygon(30% 0%, 100% 0%, 100% 100%, 0% 100%);
  }
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  gap: 2.5rem;
  align-items: center;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const HeroContent = styled.div`
  z-index: 1;
  padding-right: 2rem;

  @media (max-width: 1024px) {
    padding-right: 0;
  }
`;

const Greeting = styled(motion.div)`
  ${({ theme }) => theme.mixins.bodyStyles};
  font-size: ${({ theme }) => theme.sizes.lg};
  color: ${({ theme }) => theme.primary};
  margin-top: 3.5rem;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: ${({ theme }) => theme.weights.medium};

  span {
    display: flex;
    align-items: center;
    padding: 0.5rem 1rem;
    background: ${({ theme }) => theme.primary}15;
    border-radius: 50px;
  }

  @media (max-width: 1024px) {
    justify-content: center;
  }

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.sizes.md};
    margin-top: 2rem;
  }
`;

const Title = styled(motion.h1)`
  ${({ theme }) => theme.mixins.headingStyles};
  font-size: clamp(${({ theme }) => theme.sizes['4xl']}, 5vw, ${({ theme }) => theme.sizes['6xl']});
  margin-bottom: 1.5rem;
  
  .name {
    display: block;
    margin-bottom: 0.5rem;
    
  }
  
  .highlight-container {
    height: 4rem;
    position: relative;
    display: flex;
   align-items: center;
    overflow: hidden;

    @media (max-width: 768px) {
      height: 3rem;
    }

    @media (max-width: 480px) {
      height: 2.5rem;
      
    }
  }

  .highlight {
    color: ${({ theme }) => theme.primary};
    font-size: clamp(${({ theme }) => theme.sizes['2xl']}, 4vw, ${({ theme }) => theme.sizes['4xl']});
    font-weight: ${({ theme }) => theme.weights.semibold};
    letter-spacing: -0.01em;
    
    width: 100%;
     text-align: center;
  
     @media (max-width: 1024px) {
      text-align: center;
    }
    
    @media (max-width: 480px) {
      font-size: ${({ theme }) => theme.sizes.lg};
}}
`;

const Description = styled(motion.p)`
  ${({ theme }) => theme.mixins.bodyStyles};
  font-size: ${({ theme }) => theme.sizes.lg};
  color: ${({ theme }) => theme.text}dd;
  margin-bottom: 2.5rem;
  max-width: 600px;
  line-height: ${({ theme }) => theme.lineHeights.relaxed};

  @media (max-width: 1024px) {
    margin: 0 auto 2.5rem;
  }

  @media (max-width: 480px) {
    font-size: ${({ theme }) => theme.sizes.md};
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  padding: 0rem 5rem;
  gap: 1.5rem;

  @media (max-width: 1024px) {
    justify-content: center;
    padding: 0rem 2rem;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 1rem;
    padding: 0rem 1rem;
  }
`;

const Button = styled(motion.a)`
  ${({ theme }) => theme.mixins.bodyStyles};
  padding: 0.7rem 1.2rem;
  border-radius: 32px;
  font-size: ${({ theme }) => theme.sizes.lg};
  font-weight: ${({ theme }) => theme.weights.semibold};
  letter-spacing: 0.02em;
  cursor: pointer;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.8rem;
  position: relative;
  overflow: hidden;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 120%;
    height: 120%;
    background: radial-gradient(circle, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
    transform: translate(-50%, -50%) scale(0);
    transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 1;
  }

  &:hover::before {
    transform: translate(-50%, -50%) scale(1);
  }

  span {
    position: relative;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 0.8rem;
  }

  &.primary {
    background: linear-gradient(
      135deg,
      ${({ theme }) => theme.primary} 0%,
      ${({ theme }) => theme.primary}ee 100%
    );
    color: ${({ theme }) => theme.background};
    box-shadow: 0 4px 20px ${({ theme }) => theme.primary}40,
                0 8px 30px ${({ theme }) => theme.primary}30;
    border: 2px solid transparent;

    &:hover {
      transform: translateY(-3px) scale(1.02);
      box-shadow: 0 6px 25px ${({ theme }) => theme.primary}50,
                  0 10px 35px ${({ theme }) => theme.primary}40;
    }

    &:active {
      transform: translateY(1px) scale(0.98);
    }
  }

  &.secondary {
    background: ${({ theme }) => theme.background};
    color: ${({ theme }) => theme.primary};
    border: 2px solid ${({ theme }) => theme.primary}30;
    backdrop-filter: blur(10px);
    
    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: ${({ theme }) => theme.primary}10;
      transform: scaleX(0);
      transform-origin: right;
      transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
      z-index: 0;
    }

    &:hover {
      transform: translateY(-3px) scale(1.02);
      border-color: ${({ theme }) => theme.primary};
      box-shadow: 0 6px 25px ${({ theme }) => theme.primary}20;

      &::after {
        transform: scaleX(1);
        transform-origin: left;
      }
    }

    &:active {
      transform: translateY(1px) scale(0.98);
    }
  }

  @media (max-width: 768px) {
    padding: 0.9rem 2rem;
    font-size: ${({ theme }) => theme.sizes.md};
  }

  @media (max-width: 480px) {
    width: 100%;
    padding: 1rem 1.5rem;
  }
`;

const ImageWrapper = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  min-height: 450px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1000px;

  @media (max-width: 1024px) {
    min-height: 400px;
  }

  @media (max-width: 480px) {
    min-height: 350px;
  }
`;

const ImageContainer = styled(motion.div)`
  position: relative;
  width: 90%;
  height: 90%;
  transform-style: preserve-3d;

  &::before,
  &::after {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
    background: ${({ theme }) => theme.primary}15;
    z-index: 0;
  }

  &::before {
    transform: translateZ(-50px);
    filter: blur(20px);
    opacity: 0.8;
  }

  &::after {
    transform: translateZ(-25px);
    filter: blur(10px);
    opacity: 0.4;
  }
`;

const ImageFrame = styled(motion.div)`
  position: relative;
  width: 100%;
  height: 100%;
  border-radius: 30% 70% 70% 30% / 30% 30% 70% 70%;
  overflow: hidden;
  box-shadow: 
    0 10px 30px ${({ theme }) => theme.primary}30,
    0 20px 60px ${({ theme }) => theme.primary}20;
  
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      45deg,
      ${({ theme }) => theme.primary}20 0%,
      transparent 100%
    );
    z-index: 2;
  }
`;

const StyledImage = styled(motion.img)`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const FloatingParticle = styled(motion.div)`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.primary};
  opacity: 0.2;
  z-index: 1;
`;

const Hero = () => {
  const [roleIndex, setRoleIndex] = useState(0);
  const roles = [
    "Full Stack Developer",
    "Backend Developer",
    "Mobile App Developer",
    "Freelancer"
  ];
  const particles = Array.from({ length: 15 }, (_, i) => ({
    id: i,
    size: Math.random() * 10 + 5,
    initialX: Math.random() * 100,
    initialY: Math.random() * 100,
  }));
  const imageVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: -20 },
    visible: {
      opacity: 1,
      scale: 1,
      rotateY: 0,
      transition: {
        duration: 1,
        ease: "easeOut",
      }
    },
    hover: {
      scale: 1.05,
      rotateY: 10,
      transition: {
        duration: 0.5,
      }
    }
  };

  const particleVariants = {
    animate: {
      x: [0, 100, 0],
      y: [0, 50, 0],
      transition: {
        duration: random(3, 6),
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
      }
    }
  };

  function random(min, max) {
    return Math.random() * (max - min) + min;
  }
  useEffect(() => {
    const interval = setInterval(() => {
      setRoleIndex((prevIndex) => (prevIndex + 1) % roles.length);
    }, 2000);

    return () => clearInterval(interval);
  }, [roles.length]);

  const handleDownloadResume = () => {
    const link = document.createElement('a');
    link.href = ResumePDF;
    link.download = 'Ewnetu_Tesfaye_Resume.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <HeroSection id="home">
      <HeroContainer>
        <HeroContent>
          <Greeting
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <span>👋 Hello, I'm</span>
          </Greeting>
          <Title
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <span className="name">Ewnetu Tesfaye</span>
            <div className="highlight-container">
              <AnimatePresence mode='wait'>
                <motion.span
                  key={roleIndex}
                  className="highlight"
                  initial={{ y: 50, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  exit={{ y: -50, opacity: 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {roles[roleIndex]}
                </motion.span>
              </AnimatePresence>
            </div>
          </Title>
          <Description
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
           Expertise in MERNstack:Crafting dynamic web experiences with Mongodb Express.js React and Node.js. Bringing ideas to life with seamless integration top-notch performance and a passion for clean scalable code.
          </Description>
          <ButtonGroup
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.6 }}
          >
            <Button 
              href="#contact"
              className="primary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                Let's Talk
                <motion.span
                  initial={{ rotate: 0 }}
                  animate={{ rotate: [0, -20, 20, -20, 20, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                >
                  👋
                </motion.span>
              </span>
            </Button>
            <Button 
              as="button"
              onClick={handleDownloadResume}
              className="secondary"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <span>
                Resume
                <motion.span
                  initial={{ x: 0 }}
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity, repeatDelay: 3 }}
                >
                  🚀
                </motion.span>
              </span>
            </Button>
          </ButtonGroup>
        </HeroContent>
        <ImageWrapper>
          <ImageContainer
            initial="hidden"
            animate="visible"
            whileHover="hover"
            variants={imageVariants}
          >
            {particles.map((particle) => (
              <FloatingParticle
                key={particle.id}
                size={particle.size}
                style={{
                  left: `${particle.initialX}%`,
                  top: `${particle.initialY}%`,
                }}
                animate={{
                  x: [0, random(-50, 50), 0],
                  y: [0, random(-50, 50), 0],
                  opacity: [0.2, 0.5, 0.2],
                }}
                transition={{
                  duration: random(3, 6),
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            ))}
            <ImageFrame
              animate={{
                borderRadius: [
                  "30% 70% 70% 30% / 30% 30% 70% 70%",
                  "70% 30% 30% 70% / 70% 70% 30% 30%",
                  "30% 70% 70% 30% / 30% 30% 70% 70%",
                ],
              }}
              transition={{
                duration: 8,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut",
              }}
            >
              <StyledImage
                src={HomeImg}
                alt="Profile"
                initial={{ scale: 1.2 }}
                animate={{
                  scale: [1.2, 1.15, 1.2],
                }}
                transition={{
                  duration: 8,
                  repeat: Infinity,
                  repeatType: "reverse",
                  ease: "easeInOut",
                }}
              />
            </ImageFrame>
          </ImageContainer>
        </ImageWrapper>
      </HeroContainer>
    </HeroSection>
  );
};

export default Hero;